import { DEVICE } from '@/shared/config';
import type { RmaOptionsQueryVariables } from '@/shared/api';
import type { RmaOptionLists } from '../../model/rma.types';
import fetchRmaOptions from './fetchRmaOptions';

const REASON_DEVICE_MODELS = [DEVICE.BIOFLUX, DEVICE.BIOTRES, DEVICE.BIOTRES_PRO];

/**
 * Loads one RMA option list without letting its failure reject the session bootstrap. Logs the error and falls back to an empty list when the query throws.
 *
 * @param variables - Category and optional device model of the list.
 * @returns A promise resolving to the options, empty when the query failed.
 */
const fetchRmaOptionsOrEmpty = (variables: RmaOptionsQueryVariables) =>
  fetchRmaOptions(variables).catch((error) => {
    console.error('Failed to fetch RMA options: ', error);
    return [];
  });

/**
 * Loads the RMA option lists the session keeps: the patient impacts and the reasons of every device model. Queries each list in parallel, and a list whose query fails is left empty so the others still load.
 *
 * @returns A promise resolving to the patient impacts and the reasons keyed by device model.
 */
const fetchRmaOptionLists = async (): Promise<RmaOptionLists> => {
  const [patientImpacts, ...reasonLists] = await Promise.all([
    fetchRmaOptionsOrEmpty({ category: 'PATIENT_IMPACT' }),
    ...REASON_DEVICE_MODELS.map((deviceModel) =>
      fetchRmaOptionsOrEmpty({ category: 'REASON', deviceModel }),
    ),
  ]);
  return {
    reasons: Object.fromEntries(REASON_DEVICE_MODELS.map((model, i) => [model, reasonLists[i]])),
    patientImpacts,
  };
};

export default fetchRmaOptionLists;
